import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { TOOLS } from "@/components/ToolPanels";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

const lsGet = (k, d) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } };

export default function Tools() {
  const [active, setActive] = useState(null);
  const [favs, setFavs] = useState(() => lsGet("fgp_tool_favs", []));
  useEffect(() => { lsSet("fgp_tool_favs", favs); }, [favs]);

  const toggleFav = (id) => setFavs((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]);
  const ordered = [...TOOLS].sort((a, b) => (favs.includes(b.id) ? 1 : 0) - (favs.includes(a.id) ? 1 : 0));
  const activeTool = TOOLS.find((t) => t.id === active);

  return (
    <div>
      <PageHeader title="Tools" subtitle="A handy toolbox for the everyday — calculators, converters and planners that work in real time." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ordered.map((t, i) => (
          <motion.button
            key={t.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            onClick={() => setActive(t.id)}
            data-testid={`tool-card-${t.id}`}
            className="group relative text-left bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-neutral-200 transition-all"
          >
            <span
              onClick={(e) => { e.stopPropagation(); toggleFav(t.id); }}
              data-testid={`tool-fav-${t.id}`}
              className={`absolute top-3 right-3 cursor-pointer ${favs.includes(t.id) ? "text-amber-400" : "text-slate-300 hover:text-slate-400"}`}
            >
              <Star className="h-4 w-4" fill={favs.includes(t.id) ? "currentColor" : "none"} />
            </span>
            <div className="h-11 w-11 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center group-hover:bg-neutral-600 group-hover:text-white transition-colors">
              <t.icon className="h-5 w-5" />
            </div>
            <p className="mt-4 font-heading font-semibold text-slate-900">{t.name}</p>
            <p className="text-sm text-slate-400 mt-0.5">{t.desc}</p>
          </motion.button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="tool-panel">
          {activeTool && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-neutral-600 text-white flex items-center justify-center"><activeTool.icon className="h-5 w-5" /></div>
                  <div><DialogTitle className="font-heading">{activeTool.name}</DialogTitle><DialogDescription>{activeTool.desc}</DialogDescription></div>
                </div>
              </DialogHeader>
              <div className="pt-2"><activeTool.Component /></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
